<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import WhatsappManagementAPI from 'dashboard/api/whatsappManagement';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({ inbox: { type: Object, required: true } });
const { t } = useI18n();
const groups = ref([]);
const selected = ref(null);
const participants = ref([]);
const audits = ref([]);
const loading = ref(false);
const creating = ref(false);
const participantNumbers = ref('');
const inviteNumbers = ref('');
const inviteDescription = ref('');
const inviteCode = ref('');
const form = ref({
  subject: '',
  description: '',
  internal_note: '',
  picture: '',
});
const createForm = ref({ subject: '', description: '', participants: '' });

const selectedJid = computed(() => selected.value?.jid || selected.value?.id);
const groupName = group => group.subject || group.name || group.jid || group.id;
const splitNumbers = value =>
  value
    .split(/[\s,;]+/)
    .map(item => item.trim())
    .filter(Boolean);
const errorMessage = error =>
  error.response?.data?.error || error.response?.data?.message || error.message;

const fileToBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',').pop());
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const refresh = async () => {
  loading.value = true;
  try {
    const [{ data: groupData }, { data: auditData }] = await Promise.all([
      WhatsappManagementAPI.groups(props.inbox.id),
      WhatsappManagementAPI.audits(props.inbox.id),
    ]);
    groups.value = groupData.payload || [];
    audits.value = auditData.payload || [];
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    loading.value = false;
  }
};

const selectGroup = async group => {
  loading.value = true;
  try {
    const jid = group.jid || group.id;
    const [{ data: details }, { data: participantData }] = await Promise.all([
      WhatsappManagementAPI.group(props.inbox.id, jid),
      WhatsappManagementAPI.participants(props.inbox.id, jid),
    ]);
    selected.value = details;
    participants.value = participantData.participants || [];
    form.value = {
      subject: details.subject || details.name || '',
      description: details.desc || details.description || '',
      internal_note: details.internal_note || '',
      picture: '',
    };
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    loading.value = false;
  }
};

const createGroup = async () => {
  try {
    const { data } = await WhatsappManagementAPI.createGroup(props.inbox.id, {
      subject: createForm.value.subject,
      description: createForm.value.description,
      participants: splitNumbers(createForm.value.participants),
    });
    creating.value = false;
    createForm.value = { subject: '', description: '', participants: '' };
    await refresh();
    await selectGroup(data);
    useAlert(t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SAVED'));
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const saveGroup = async () => {
  try {
    const { data } = await WhatsappManagementAPI.updateGroup(
      props.inbox.id,
      selectedJid.value,
      form.value
    );
    selected.value = data;
    await refresh();
    useAlert(t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SAVED'));
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const onPicture = async event => {
  const [file] = event.target.files;
  if (file) form.value.picture = await fileToBase64(file);
};

const changeParticipants = async (action, values) => {
  const numbers = Array.isArray(values) ? values : splitNumbers(values);
  if (!numbers.length) return;
  try {
    await WhatsappManagementAPI.updateParticipants(
      props.inbox.id,
      selectedJid.value,
      action,
      numbers
    );
    participantNumbers.value = '';
    await selectGroup(selected.value);
    useAlert(t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SAVED'));
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const loadInvite = async () => {
  try {
    const { data } = await WhatsappManagementAPI.invite(
      props.inbox.id,
      selectedJid.value
    );
    inviteCode.value =
      data.inviteUrl || data.inviteCode || data.code || JSON.stringify(data);
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const revokeInvite = async () => {
  try {
    await WhatsappManagementAPI.revokeInvite(props.inbox.id, selectedJid.value);
    inviteCode.value = '';
    await loadInvite();
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const sendInvite = async () => {
  try {
    await WhatsappManagementAPI.sendInvite(props.inbox.id, selectedJid.value, {
      description: inviteDescription.value,
      numbers: splitNumbers(inviteNumbers.value),
    });
    inviteNumbers.value = '';
    useAlert(t('INBOX_MGMT.WHATSAPP_MANAGEMENT.INVITE_SENT'));
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const setGroupOption = async action => {
  try {
    await WhatsappManagementAPI.setting(
      props.inbox.id,
      selectedJid.value,
      action
    );
    useAlert(t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SAVED'));
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const setEphemeral = async event => {
  try {
    await WhatsappManagementAPI.ephemeral(
      props.inbox.id,
      selectedJid.value,
      Number(event.target.value)
    );
    useAlert(t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SAVED'));
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const leaveGroup = async () => {
  if (!window.confirm(t('INBOX_MGMT.WHATSAPP_MANAGEMENT.LEAVE_CONFIRM')))
    return;
  try {
    await WhatsappManagementAPI.leave(props.inbox.id, selectedJid.value);
    selected.value = null;
    await refresh();
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

onMounted(refresh);
</script>

<template>
  <div class="grid gap-6 lg:grid-cols-[20rem_1fr]">
    <aside class="rounded-xl border border-n-weak bg-n-surface-1 p-4">
      <div class="mb-4 flex items-center justify-between gap-2">
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.GROUPS') }}
        </h3>
        <NextButton
          icon="i-lucide-plus"
          size="sm"
          @click="creating = !creating"
        />
      </div>
      <div v-if="creating" class="mb-4 space-y-3 rounded-lg bg-n-alpha-2 p-3">
        <input
          v-model="createForm.subject"
          class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
          :placeholder="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SUBJECT')"
        />
        <textarea
          v-model="createForm.description"
          class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
          :placeholder="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.DESCRIPTION')"
        />
        <textarea
          v-model="createForm.participants"
          class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
          :placeholder="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.NUMBERS_HINT')"
        />
        <NextButton
          :label="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.CREATE')"
          :disabled="!createForm.subject || !createForm.participants"
          @click="createGroup"
        />
      </div>
      <div class="space-y-1">
        <button
          v-for="groupItem in groups"
          :key="groupItem.jid || groupItem.id"
          class="w-full rounded-lg px-3 py-2 text-left hover:bg-n-alpha-2"
          :class="
            selectedJid === (groupItem.jid || groupItem.id)
              ? 'bg-n-alpha-2'
              : ''
          "
          @click="selectGroup(groupItem)"
        >
          <span class="block font-medium text-n-slate-12">{{
            groupName(groupItem)
          }}</span>
          <span class="block truncate text-xs text-n-slate-10">{{
            groupItem.jid || groupItem.id
          }}</span>
        </button>
        <p
          v-if="!loading && !groups.length"
          class="py-4 text-sm text-n-slate-10"
        >
          {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.NO_GROUPS') }}
        </p>
      </div>
    </aside>

    <main v-if="selected" class="space-y-6">
      <section class="rounded-xl border border-n-weak bg-n-surface-1 p-5">
        <h3 class="mb-4 text-base font-semibold text-n-slate-12">
          {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.INFORMATION') }}
        </h3>
        <div class="grid gap-4 md:grid-cols-2">
          <label class="text-sm text-n-slate-11">
            <span>{{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SUBJECT') }}</span>
            <input
              v-model="form.subject"
              class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
            />
          </label>
          <label class="text-sm text-n-slate-11">
            <span>{{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.PICTURE') }}</span>
            <input
              type="file"
              accept="image/*"
              class="mt-1 block w-full text-sm"
              @change="onPicture"
            />
          </label>
          <label class="text-sm text-n-slate-11 md:col-span-2">
            <span>{{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.DESCRIPTION') }}</span>
            <textarea
              v-model="form.description"
              class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
            />
          </label>
          <label class="text-sm text-n-slate-11 md:col-span-2">
            <span>{{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.INTERNAL_NOTE') }}</span>
            <textarea
              v-model="form.internal_note"
              class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
            />
          </label>
        </div>
        <NextButton
          class="mt-4"
          :label="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SAVE')"
          @click="saveGroup"
        />
      </section>

      <section class="rounded-xl border border-n-weak bg-n-surface-1 p-5">
        <h3 class="mb-4 text-base font-semibold text-n-slate-12">
          {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.PARTICIPANTS') }}
        </h3>
        <div class="mb-4 flex gap-2">
          <input
            v-model="participantNumbers"
            class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-background px-3 py-2"
            :placeholder="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.NUMBERS_HINT')"
          /><NextButton
            :label="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.ADD')"
            @click="changeParticipants('add', participantNumbers)"
          />
        </div>
        <div class="divide-y divide-n-weak">
          <div
            v-for="participant in participants"
            :key="participant.jid"
            class="flex flex-wrap items-center justify-between gap-2 py-3"
          >
            <div>
              <p class="font-medium text-n-slate-12">
                {{
                  participant.saved_name ||
                  participant.name ||
                  participant.phone_number
                }}
              </p>
              <p class="text-xs text-n-slate-10">
                {{ participant.jid }} ·
                {{
                  participant.admin ||
                  t('INBOX_MGMT.WHATSAPP_MANAGEMENT.MEMBER')
                }}
              </p>
            </div>
            <div class="flex gap-1">
              <NextButton
                size="sm"
                variant="ghost"
                :label="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.PROMOTE')"
                @click="changeParticipants('promote', [participant.jid])"
              /><NextButton
                size="sm"
                variant="ghost"
                :label="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.DEMOTE')"
                @click="changeParticipants('demote', [participant.jid])"
              /><NextButton
                size="sm"
                variant="ghost"
                color="ruby"
                :label="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.REMOVE')"
                @click="changeParticipants('remove', [participant.jid])"
              />
            </div>
          </div>
        </div>
      </section>

      <section class="grid gap-6 xl:grid-cols-2">
        <div class="rounded-xl border border-n-weak bg-n-surface-1 p-5">
          <h3 class="mb-4 font-semibold text-n-slate-12">
            {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.INVITES') }}
          </h3>
          <div class="flex gap-2">
            <NextButton
              :label="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.GET_LINK')"
              @click="loadInvite"
            /><NextButton
              variant="outline"
              :label="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.REVOKE_LINK')"
              @click="revokeInvite"
            />
          </div>
          <p
            v-if="inviteCode"
            class="mt-3 break-all rounded-lg bg-n-alpha-2 p-3 text-sm"
          >
            {{ inviteCode }}
          </p>
          <input
            v-model="inviteNumbers"
            class="mt-4 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
            :placeholder="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.NUMBERS_HINT')"
          />
          <textarea
            v-model="inviteDescription"
            class="mt-2 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
            :placeholder="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.INVITE_MESSAGE')"
          />
          <NextButton
            class="mt-2"
            :label="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SEND_INVITE')"
            @click="sendInvite"
          />
        </div>
        <div class="rounded-xl border border-n-weak bg-n-surface-1 p-5">
          <h3 class="mb-4 font-semibold text-n-slate-12">
            {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.PERMISSIONS') }}
          </h3>
          <div class="flex flex-wrap gap-2">
            <NextButton
              variant="outline"
              :label="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.ONLY_ADMINS')"
              @click="setGroupOption('announcement')"
            /><NextButton
              variant="outline"
              :label="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.EVERYONE_SENDS')"
              @click="setGroupOption('not_announcement')"
            /><NextButton
              variant="outline"
              :label="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.LOCK_INFO')"
              @click="setGroupOption('locked')"
            /><NextButton
              variant="outline"
              :label="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.UNLOCK_INFO')"
              @click="setGroupOption('unlocked')"
            />
          </div>
          <label class="mt-4 block text-sm text-n-slate-11">
            <span>{{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.EPHEMERAL') }}</span>
            <select
              class="mt-1 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2"
              @change="setEphemeral"
            >
              <option value="0">
                {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.OFF') }}
              </option>
              <option value="86400">
                {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.TWENTY_FOUR_HOURS') }}
              </option>
              <option value="604800">
                {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SEVEN_DAYS') }}
              </option>
              <option value="7776000">
                {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.NINETY_DAYS') }}
              </option>
            </select>
          </label>
          <NextButton
            class="mt-6"
            color="ruby"
            variant="outline"
            :label="t('INBOX_MGMT.WHATSAPP_MANAGEMENT.LEAVE')"
            @click="leaveGroup"
          />
        </div>
      </section>

      <section class="rounded-xl border border-n-weak bg-n-surface-1 p-5">
        <h3 class="mb-3 font-semibold text-n-slate-12">
          {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.HISTORY') }}
        </h3>
        <div
          v-for="audit in audits
            .filter(item => item.resource_id === selectedJid)
            .slice(0, 20)"
          :key="audit.id"
          class="border-b border-n-weak py-2 text-sm"
        >
          <span class="font-medium">{{ audit.action }}</span> ·
          {{ audit.user_name || 'Sistema' }} ·
          {{ new Date(audit.created_at).toLocaleString()
          }}<span v-if="!audit.success" class="ml-2 text-n-ruby-9">{{
            audit.error_message
          }}</span>
        </div>
      </section>
    </main>
    <div
      v-else
      class="rounded-xl border border-dashed border-n-weak p-10 text-center text-n-slate-10"
    >
      {{ t('INBOX_MGMT.WHATSAPP_MANAGEMENT.SELECT_GROUP') }}
    </div>
  </div>
</template>
